/**
 * Pure H OS · Phase D Wave 3 · OKRs.
 * Objective + Key Result tracking for the current cycle.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { OKRS, OkrStatus } from "../mocks/okrsData";

const STATUS_LABEL: Record<OkrStatus, string> = {
  "on-track": "On track", "at-risk": "At risk", "off-track": "Off track", "done": "Done",
};
function statusSeverity(s: OkrStatus): "low" | "medium" | "high" {
  if (s === "on-track" || s === "done") return "low";
  if (s === "at-risk") return "medium";
  return "high";
}

type Tab = "all" | OkrStatus;

export default function OKRs() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => (tab === "all" ? OKRS : OKRS.filter((o) => o.status === tab)), [tab]);
  const current = openId ? OKRS.find((o) => o.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="okrs-page">
      <Card testId="okr-filters">
        <TabBar<Tab>
          testId="okr-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",        label: `All ${OKRS.length}` },
            { key: "on-track",   label: "On track" },
            { key: "at-risk",    label: "At risk" },
            { key: "off-track",  label: "Off track" },
            { key: "done",       label: "Done" },
          ]}
        />
      </Card>

      <Card padded={false} testId="okr-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Objectives & Key Results" title={`${filtered.length} objective${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Cycle {OKRS[0]?.cycle ?? "—"} · one owner per objective.</div>
        </div>
        <div className="p-2">
          {filtered.map((o) => (
            <ListRow key={o.id} testId={`okr-${o.id}`}
              onClick={() => setOpenId(o.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{o.function}</span>
                  <span>{o.objective}</span>
                </span>
              }
              sublabel={<>{o.owner} · {o.progressPct}% · {STATUS_LABEL[o.status]}</>}
              trailing={<SeverityBadge severity={statusSeverity(o.status)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="okr-drawer" title={current ? current.objective : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${current.function} · ${current.cycle}`} title={current.owner} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Progress</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.progressPct}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Status</div>
                <div className="mt-2"><SeverityBadge severity={statusSeverity(current.status)} /></div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>Key Results</div>
              <div className="space-y-3">
                {current.kpis.map((k, i) => (
                  <div key={i} data-testid={`okr-kr-${i}`} className="p-3 rounded" style={{ background: "var(--ph-surface-subtle)" }}>
                    <div className="text-xs font-semibold" style={{ color: "var(--ph-text)" }}>{k.label}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ph-muted)" }}>
                      Target: <span style={{ color: "var(--ph-text)" }}>{k.target}</span> · Current: <span style={{ color: "var(--ph-text)" }}>{k.current}</span> · {k.progressPct}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
